import { useEffect, useRef, useState } from 'react'
import { withDebounce } from '../utils'

/**
 * Custom hook that measures the height of a container element and returns it.
 *
 * @template T The type of the container element.
 * @returns An object containing the container height and a ref to the container element.
 */
export const useResizeObserver = <T extends HTMLDivElement>() => {
    const treeContainerRef = useRef<T | null>(null)
    const [containerHeight, setContainerHeight] = useState(0)

    // side-effect to measure height dynamically
    useEffect(() => {
        let resizeObserver: ResizeObserver | null = null
        let isMounted = true
        
        const handleResize = withDebounce<ResizeObserverCallback>(
            (observer) => {
                if (observer[0] && isMounted) {
                    const { height } = observer[0].contentRect
                    setContainerHeight(height)
                }
            }
        )

        try {
            resizeObserver = new ResizeObserver(handleResize)

            if (treeContainerRef.current) {
                resizeObserver.observe(treeContainerRef.current)
            }
        } catch (error) {
            console.log('ResizeObserver error:', error)
        }

        return () => {
            isMounted = false
            handleResize.cancel()
            if (resizeObserver && treeContainerRef?.current) {
                resizeObserver.unobserve(treeContainerRef.current)
                resizeObserver.disconnect()
            }
        }
    }, [])

    return {
        treeContainerRef,
        containerHeight,
    }
}
